import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
  Slide,
} from "@mui/material";
import {
  Close as CloseIcon,
  Balance as DrawIcon,
  PlayArrow as ContinueIcon,
  Flag as SurrenderIcon,
  InfoOutlined as InfoIcon,
  TrendingUp as WinIcon,
  TrendingDown as LossIcon,
} from "@mui/icons-material";

import { useTranslation } from "react-i18next";
import Profile from "./Profile.js";

const GameInfoDialog = ({
  open,
  onClose,
  gameInfo,
  operations,
  onContinue,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [levelData, setLevelData] = useState(null);

  useEffect(() => {
    if (open) {
      const storedLevels = JSON.parse(localStorage.getItem("levels") || "[]");

      if (!storedLevels || !Array.isArray(storedLevels)) {
        console.error("No levels found in localStorage or incorrect format.");
        return;
      }

      const lastSelectedLevelId = localStorage.getItem("lastSelectedLevelId");
      const levelsObj = storedLevels.find(
        (level) => level.levelTypeId === +lastSelectedLevelId
      );

      if (levelsObj) {
        setLevelData(levelsObj);
      }
    }
  }, [open]);

  const handleAvatarClick = (avatar) => {
    setSelectedAvatar(avatar);
    setDialogOpen(true);
  };

  const onSurrender = () => {
    onClose();
  };

  const RewardCard = ({ title, coins, color, icon, type }) => {
    const displayCoins = type === 'loss' ? `-${Math.abs(coins)}` : `${coins}`;
    
    return (
      <Card
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
          border: `2px solid ${color}30`,
          borderRadius: isMobile ? "16px" : "20px",
          overflow: "hidden",
          position: "relative",
          height: isMobile ? "140px" : "160px",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: `0 8px 30px ${color}40`,
            border: `2px solid ${color}60`,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
          },
        }}
      >
        <CardContent 
          sx={{ 
            p: isMobile ? 2 : 2.5,
            height: "100%",
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center",
            justifyContent: "space-between",
            textAlign: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: isMobile ? 48 : 56,
              height: isMobile ? 48 : 56,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
              color: "white",
              boxShadow: `0 4px 16px ${color}50`,
              mb: 1,
            }}
          >
            {React.cloneElement(icon, { 
              sx: { fontSize: isMobile ? "1.4rem" : "1.6rem" } 
            })}
          </Box>
          
          <Typography
            variant="subtitle2"
            sx={{
              color: color,
              fontWeight: 600,
              fontSize: isMobile ? "0.8rem" : "0.9rem",
              lineHeight: 1.2,
              mb: 1,
              textAlign: "center",
              minHeight: isMobile ? "32px" : "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {title}
          </Typography>
          
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            gap: 0.5,
          }}>
            <Box
              component="img"
              src="https://res.cloudinary.com/dnlgtek2a/image/upload/v1743405234/Pictures/coin.png"
              alt="Coins"
              sx={{ 
                width: isMobile ? 20 : 24, 
                height: isMobile ? 20 : 24,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: color,
                fontSize: isMobile ? "1.1rem" : "1.3rem",
                textShadow: `0 2px 4px ${color}30`,
              }}
            >
              {displayCoins}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ flexGrow: 1, zIndex: 100 }}>
      <Profile
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        avatar={selectedAvatar}
      />
      
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth={isMobile ? false : "sm"}
        fullWidth={!isMobile}
        sx={{
          zIndex: 3000,
          "& .MuiDialog-paper": isMobile ? {
            margin: 0,
            borderRadius: 0,
            background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
            height: "100vh",
            maxHeight: "100vh",
          } : {
            borderRadius: "24px",
            background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
            boxShadow: "0 20px 60px rgba(102, 126, 234, 0.3)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            maxWidth: "500px",
            margin: "16px",
          },
        }}
      >
        <Slide direction="down" in={open} timeout={400}>
          <Box>
            <DialogTitle
              sx={{
                background: `linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)`,
                color: "white",
                textAlign: "center",
                position: "relative",
                p: isMobile ? 2 : 3,
                borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between",
                gap: 2,
              }}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1.5, 
                  flex: 1, 
                  justifyContent: "center" 
                }}>
                  <Box
                    sx={{
                      width: isMobile ? 32 : 40,
                      height: isMobile ? 32 : 40,
                      borderRadius: "50%",
                      background: "rgba(255, 255, 255, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <InfoIcon fontSize={isMobile ? "medium" : "large"} />
                  </Box>
                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    sx={{
                      fontWeight: 700,
                      fontSize: isMobile ? "1.1rem" : "1.4rem",
                      textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    }}
                  >
                    {t("Game Info")}
                  </Typography>
                </Box>
                
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{
                    color: "white",
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    "&:hover": { 
                      bgcolor: "rgba(255, 255, 255, 0.3)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent 
              sx={{ 
                p: isMobile ? 2 : 3,
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(20px)",
                minHeight: isMobile ? "auto" : "300px",
              }}
            >
              {gameInfo ? (
                <Box>
                  {levelData && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          textAlign: "center",
                          mb: 3,
                          fontSize: isMobile ? "1rem" : "1.2rem",
                          textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                        }}
                      >
                        💰 {t("Reward Structure")}
                      </Typography>

                      <Grid container spacing={isMobile ? 1.5 : 2}>
                        <Grid item xs={12} sm={4}>
                          <RewardCard
                            title={t("Victory")}
                            coins={levelData?.winCoins || 0}
                            color="#4caf50"
                            icon={<WinIcon />}
                            type="win"
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={4}>
                          <RewardCard
                            title={t("draw")}
                            coins={levelData?.drawCoins || 0}
                            color="#ff9800"
                            icon={<DrawIcon />}
                            type="draw"
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={4}>
                          <RewardCard
                            title={t("defeat_1")}
                            coins={levelData?.loseCoins || 0}
                            color="#f44336"
                            icon={<LossIcon />}
                            type="loss"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  
                  <Divider 
                    sx={{ 
                      my: 3,
                      background: "rgba(255, 255, 255, 0.2)",
                      height: "1px"
                    }} 
                  />
                  
                  <Box
                    sx={{
                      background: `linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)`,
                      borderRadius: "16px",
                      p: isMobile ? 2 : 2.5,
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(20px)",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "rgba(255, 255, 255, 0.9)",
                        fontSize: isMobile ? "0.85rem" : "0.95rem",
                        lineHeight: 1.6,
                        fontWeight: 500,
                      }}
                    >
                      ⚡ {t("Choose your strategy wisely! Each game outcome affects your coin balance.")}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    p: 4,
                    color: "rgba(255, 255, 255, 0.8)",
                    minHeight: "200px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <InfoIcon sx={{ fontSize: "3rem", mb: 2, opacity: 0.6 }} />
                  <Typography variant="h6" sx={{ mb: 1, fontSize: isMobile ? "1rem" : "1.2rem" }}>
                    {t("No game information available")}
                  </Typography>
                </Box>
              )}
            </DialogContent>

            <DialogActions 
              sx={{ 
                p: isMobile ? 2 : 2.5,
                gap: isMobile ? 1 : 1.5,
                background: "rgba(255, 255, 255, 0.1)",
                borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(20px)",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "center"
              }}
            >
              <Stack 
                direction={isMobile ? "column" : "row"} 
                spacing={isMobile ? 1 : 1.5}
                width={isMobile ? "100%" : "auto"}
              >
                <Button
                  onClick={onClose}
                  variant="contained"
                  startIcon={<CloseIcon />}
                  fullWidth={isMobile}
                  sx={{
                    background: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
                    color: "white",
                    fontWeight: 600,
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 1.5 : 1.2,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontSize: isMobile ? "0.9rem" : "1rem",
                    boxShadow: "0 4px 16px rgba(100, 116, 139, 0.4)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #475569 0%, #334155 100%)",
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 20px rgba(100, 116, 139, 0.5)",
                    },
                  }}
                >
                  {t("Close")}
                </Button>

                {operations && (
                  <>
                    <Button
                      onClick={onContinue}
                      variant="contained"
                      startIcon={<ContinueIcon />}
                      fullWidth={isMobile}
                      sx={{
                        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        color: "white",
                        fontWeight: 600,
                        px: isMobile ? 2 : 3,
                        py: isMobile ? 1.5 : 1.2,
                        borderRadius: "12px",
                        textTransform: "none",
                        fontSize: isMobile ? "0.9rem" : "1rem",
                        boxShadow: "0 4px 16px rgba(16, 185, 129, 0.4)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                          transform: "translateY(-1px)",
                          boxShadow: "0 6px 20px rgba(16, 185, 129, 0.5)",
                        },
                      }}
                    >
                      {t("Continue")}
                    </Button>

                    <Button
                      onClick={onSurrender}
                      variant="contained"
                      startIcon={<SurrenderIcon />}
                      fullWidth={isMobile}
                      sx={{
                        background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                        color: "white",
                        fontWeight: 600,
                        px: isMobile ? 2 : 3,
                        py: isMobile ? 1.5 : 1.2,
                        borderRadius: "12px",
                        textTransform: "none",
                        fontSize: isMobile ? "0.9rem" : "1rem",
                        boxShadow: "0 4px 16px rgba(239, 68, 68, 0.4)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                          transform: "translateY(-1px)",
                          boxShadow: "0 6px 20px rgba(239, 68, 68, 0.5)",
                        },
                      }}
                    >
                      {t("Surrender")}
                    </Button>
                  </>
                )}
              </Stack>
            </DialogActions>
          </Box>
        </Slide>
      </Dialog>
    </Box>
  );
};

export default GameInfoDialog;